"use client";

import { motion } from "framer-motion";
import { CheckCircle, Mail, MessageCircle } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import type React from "react";
import { useState } from "react";

function FacebookIcon({ size = 16, className }: { size?: number; className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  );
}

import { useIntlayer } from "next-intlayer";

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const colVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } },
};

export default function Footer() {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const t = useIntlayer("footer");

  function handleSubscribe(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribed(true);
    setEmail("");
  }

  return (
    <footer className="border-t border-border bg-background">
      <div className="container mx-auto px-4 py-16">
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {/* Column 1: Brand */}
          <motion.div variants={colVariants}>
            <Link href="/" className="flex items-center gap-2 mb-4">
              <Image src="/logo.svg" alt="" width={32} height={32} />
              <span className="text-lg font-semibold text-foreground">SomeMath</span>
            </Link>
            <p className="text-sm leading-relaxed mb-3 text-muted-foreground">{t.quote}</p>
            <p className="text-sm leading-relaxed text-muted-foreground">{t.tagline}</p>
          </motion.div>

          {/* Column 2: Courses */}
          <motion.div variants={colVariants}>
            <p className="text-xs font-semibold tracking-[0.3em] uppercase mb-5 text-[#0193cd]">
              {t.coursesHeading}
            </p>
            <ul className="space-y-3">
              {[
                { label: t.browseAllCourses, href: "/courses" },
                { label: t.myCourses, href: "/my-courses" },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    prefetch={false}
                    className="text-sm text-foreground transition-colors hover:text-[#0193cd]"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Column 3: Contact */}
          <motion.div variants={colVariants}>
            <p className="text-xs font-semibold tracking-[0.3em] uppercase mb-5 text-[#0193cd]">
              {t.contactHeading}
            </p>
            <ul className="space-y-4">
              <li>
                <a
                  href="mailto:learn@somemath.com"
                  className="flex items-center gap-3 text-sm text-foreground transition-colors hover:text-[#0193cd]"
                >
                  <Mail size={16} className="text-[#0193cd]" />
                  learn@somemath.com
                </a>
              </li>
              <li>
                <a
                  href="https://line.me/ti/p/@somemath"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-sm text-foreground transition-colors hover:text-[#0193cd]"
                >
                  <MessageCircle size={16} className="text-[#0193cd]" />
                  @somemath (Line)
                </a>
              </li>
              <li>
                <a
                  href="https://www.facebook.com/somemath"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-sm text-foreground transition-colors hover:text-[#0193cd]"
                >
                  <FacebookIcon size={16} className="text-[#0193cd]" />
                  somemath
                </a>
              </li>
            </ul>
          </motion.div>

          {/* Column 4: Newsletter */}
          <motion.div variants={colVariants}>
            <p className="text-xs font-semibold tracking-[0.3em] uppercase mb-5 text-[#0193cd]">
              {t.stayUpdatedHeading}
            </p>
            <p className="text-sm mb-4 text-muted-foreground">{t.newsletterDesc}</p>
            {subscribed ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center gap-2 text-sm text-[#0193cd]"
              >
                <CheckCircle size={18} />
                <span>{t.subscribed}</span>
              </motion.div>
            ) : (
              <form onSubmit={handleSubscribe} className="space-y-2">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.emailPlaceholder.value as string}
                  aria-label="Email address"
                  className="w-full rounded-lg px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0193cd] bg-muted border border-border text-foreground placeholder:text-muted-foreground"
                />
                <button
                  type="submit"
                  className="w-full rounded-lg px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 bg-[#0193cd]"
                >
                  {t.subscribe}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>

        {/* Bottom legal bar */}
        <div className="mt-12 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-border">
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <Link
              href="/privacy"
              prefetch={false}
              className="transition-colors hover:text-[#0193cd]"
            >
              {t.privacy}
            </Link>
            <span>·</span>
            <Link href="/terms" prefetch={false} className="transition-colors hover:text-[#0193cd]">
              {t.terms}
            </Link>
          </div>
          <p className="text-xs text-muted-foreground">{t.copyright}</p>
        </div>
      </div>
    </footer>
  );
}
